import { useState } from 'react'

import {
  GetUploadDocumentTaskFilesDocument,
  UploadDocumentTaskFileFragment,
  UploadDocumentEmployeeDocument,
} from 'generated/resources/graphql'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { type FileOptions } from 'modules/tasks/components/TaskDetails/Attachments/File'
import { TaskDetail } from 'modules/tasks/components/TaskElement/useTaskDetail'
import { useBulkUpdateUploadDocumentTaskFiles } from 'modules/tasks/hooks/useBulkUpdateUploadDocumentTaskFiles'
import { useResolveTask } from 'modules/tasks/hooks/useResolveTask'

export interface EmployeeInfo {
  firstName: string
  lastName: string
  preferredName?: string | null
  canSharePreferredName: boolean
}

interface Props {
  task: TaskDetail
  employeeId: string
  documentName?: string
  folderId?: string
  canEmployeeManageAnyEmployeeFolder: boolean
}

export const useUploadDocumentFiles = ({
  documentName,
  task,
  employeeId,
  folderId,
  canEmployeeManageAnyEmployeeFolder,
}: Props) => {
  // Only fetch folder-specific policies for upload_document tasks. Other callers
  // (e.g. IDAndSocialSecurityUploads for data_collection tasks) also pass a
  // folderId, so we cannot use folderId presence alone as the discriminator.
  const isUploadDocumentTask = task.type === 'upload_document'
  const hasFolderId = isUploadDocumentTask && !!folderId

  const { data: employeeData } = useQuery(UploadDocumentEmployeeDocument, {
    variables: {
      employeeId,
      folderIds: folderId && isUploadDocumentTask ? [folderId] : null,
      includeFolders: hasFolderId,
    },
  })

  const employeeResult = employeeData?.employees.employee

  // For upload_document tasks use the EditEmployeeFolderPolicy result for the
  // specific folder. Note: that policy also returns true for broader permissions
  // such as EditDocuments and UseCompanyDocuments — it is not limited to the
  // folder-scoped EditEmployeeDocumentsByFolder permission alone. For all other
  // task types fall back to the original general check so callers like
  // IDAndSocialSecurityUploads are unaffected.
  const canEditEmployeeDocuments = hasFolderId
    ? (employeeResult?.folders?.[0]?._policies?.edit?.can ?? false)
    : (employeeResult?._policies?.editDocuments?.can ?? false) || canEmployeeManageAnyEmployeeFolder

  const employeeInfo: EmployeeInfo | undefined = employeeResult
    ? {
        firstName: employeeResult.firstName,
        lastName: employeeResult.lastName,
        preferredName: employeeResult.preferredName,
        canSharePreferredName: employeeResult._policies?.sharePreferredName.can ?? false,
      }
    : undefined

  const { data, loading } = useQuery(GetUploadDocumentTaskFilesDocument, {
    variables: {
      taskId: task.id,
      folderId,
    },
  })

  const files = data?.tasks.uploadDocumentTaskFiles || []

  const [filesBeingUploaded, setFilesBeingUploaded] = useState<File[]>([])
  const [filesBeingRemoved, setFilesBeingRemoved] = useState<string[]>([])

  const { resolveTask } = useResolveTask()

  const updateFiles = useBulkUpdateUploadDocumentTaskFiles()
  const shouldAutoResolve = task.type === 'upload_document' && !task.completedAt

  const currentFileOptionsWitName = documentName
    ? files.filter((file) => file.filename.includes(documentName))
    : files

  const currentFileOptions: FileOptions[] = currentFileOptionsWitName
    .filter((file) => !filesBeingRemoved.includes(file.id))
    .map((file) => {
      const fileName = file.filename
      const path = buildUrl(`/${file.path}`)

      return {
        fileName,
        file,
        newFile: false,
        inlinePath: path,
        options: [
          {
            href: path,
            title: i18n.t('common.download_file_name', { name: fileName }),
          },
          ...(canEditEmployeeDocuments
            ? [
                {
                  onClick: () => onDeleteFile(file),
                  title: i18n.t('common.delete'),
                  type: 'wrong' as const,
                },
              ]
            : []),
        ],
      }
    })

  const { performAction: onAddFiles } = useAsyncAction(async (newFiles: File[]) => {
    setFilesBeingUploaded((prev) => [...prev, ...newFiles])

    const givenName = (file: File) =>
      documentName ? `${documentName}.${file.name.split('.').pop()}` : file.name

    const renamedFiles = newFiles.map(
      (file) => new File([file], givenName(file), { type: file.type })
    )

    await updateFiles({
      taskId: task.id,
      filesToAdd: renamedFiles,
      folderId,
    })

    setFilesBeingUploaded((prev) => prev.filter((f) => !newFiles.includes(f)))

    if (shouldAutoResolve)
      await resolveTask({
        variables: {
          id: task.id,
          done: true,
          identifier: task.identifier,
        },
      })
  })

  const { performAction: onDeleteFile } = useAsyncAction(
    async (removedFile: UploadDocumentTaskFileFragment) => {
      setFilesBeingRemoved((prev) => [...prev, removedFile.id])

      await updateFiles({
        taskId: task.id,
        filesToRemove: [removedFile.id],
        folderId: folderId,
      })

      setFilesBeingRemoved((prev) => prev.filter((id) => id !== removedFile.id))
    }
  )

  const uploadingFileOptions: FileOptions[] = filesBeingUploaded.map((file) => {
    const fileName = file.name
    const givenName = documentName ? `${documentName}.${fileName.split('.').pop()}` : fileName

    return {
      newFile: true,
      fileName: givenName,
      options: [],
    }
  })

  const fileOptions = [...uploadingFileOptions, ...currentFileOptions]

  return {
    employeeInfo,
    loading,
    fileOptions,
    canEditEmployeeDocuments,
    fetchedFiles: currentFileOptions,
    onAddFiles,
    onDeleteFile,
  }
}
